import React from "react";
import { useState, useEffect } from "react";
import { getPresetQuestions } from '../utils/subdomainUtils';

// SVG Icon Components
const SparklesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"/>
  </svg>
);

const FlavorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5"/>
    <path d="M8.5 8.5v.01"/>
    <path d="M16 15.5v.01"/>
    <path d="M12 12v.01"/>
    <path d="M11 17v.01"/>
    <path d="M7 14v.01"/>
  </svg>
);

const NutritionIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0l-.77.78-.77-.78a5.4 5.4 0 0 0-7.65 0C1.46 6.7 1.33 10.28 4 13l8 8 8-8c2.67-2.72 2.54-6.3.42-8.42z"/>
  </svg>
);

const DietaryIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 21h10"/>
    <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z"/>
    <path d="M11.38 12a2.4 2.4 0 0 1-.4-4.77 2.4 2.4 0 0 1 3.2-2.77 2.4 2.4 0 0 1 3.47-.63 2.4 2.4 0 0 1 3.37 3.37 2.4 2.4 0 0 1-1.1 3.7 2.51 2.51 0 0 1 .03 1.1"/>
    <path d="M13 12l-1-1"/>
    <path d="M11 10l2-2"/>
  </svg>
);

const AdventureIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
  </svg>
);

const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/>
    <path d="m21 21-4.35-4.35"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14"/>
    <path d="m12 5 7 7-7 7"/>
  </svg>
);

function InputForm({ preferences, setPreferences, onSearch, setSelected}) {
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState('suggested');
  
  // Get dynamic preset questions based on company configuration
  const presetQuestions = getPresetQuestions();

  const toggleQuestion = (question) => {
    setSelectedQuestions((prevQuestions) =>
      prevQuestions.includes(question)
        ? prevQuestions.filter((item) => item !== question)
        : [...prevQuestions, question]
    );
  };

  useEffect(() => {
    setSelected(selectedQuestions.join(" "));
  }, [selectedQuestions, setSelected]);

  // Tab configuration with icons
  const tabs = [
    { id: 'suggested', label: 'Suggested', icon: SparklesIcon },
    { id: 'flavors', label: 'Flavors', icon: FlavorIcon },
    { id: 'nutrition', label: 'Nutrition', icon: NutritionIcon },
    { id: 'dietary', label: 'Dietary', icon: DietaryIcon },
    { id: 'adventurous', label: 'Adventurous', icon: AdventureIcon }
  ];

  return (
    <div className="modern-input-container">
      <div className="input-header">
        <div className="header-decoration">
          <span className="decoration-line"></span>
          <span className="header-icon"><SparklesIcon /></span>
          <span className="decoration-line"></span>
        </div>
        <h2 className="input-title">What are you craving today?</h2>
        <p className="input-subtitle">Tell us what you're in the mood for and we'll find the perfect match</p>
        <div className="input-header-curve"></div>
      </div>

      <div className="search-section">
        <div className="search-input-wrapper">
          <div className="search-icon-left"><SearchIcon /></div>
          <input
            type="text"
            placeholder="Describe what you're craving..."
            value={preferences}
            onChange={(e) => setPreferences(e.target.value)}
            className="modern-search-input"
          />
        </div>
      </div>

      <div className="preset-section">
        <div className="section-label">Or choose from popular options</div>
        
        {/* Tab Navigation */}
        <div className="tabs-container">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="tab-icon"><IconComponent /></span>
                <span className="tab-label">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          <div className="preset-questions">
            {presetQuestions[activeTab]?.map((question, index) => (
              <button
                key={question}
                className={`preset-button ${selectedQuestions.includes(question) ? "selected" : ""}`}
                onClick={() => toggleQuestion(question)}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <span className="preset-text">{question}</span>
                {selectedQuestions.includes(question) && (
                  <span className="check-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="search-action">
        <button 
          onClick={onSearch} 
          className={`modern-search-button ${
            preferences.trim() || selectedQuestions.length > 0 ? 'active-glow' : 'disabled-state'
          }`}
          disabled={!preferences.trim() && selectedQuestions.length === 0}
        >
          <span>Find My Perfect Match</span>
          <span className="button-icon"><ArrowRightIcon /></span>
        </button>
      </div>
    </div>
  );
}

export default InputForm;
